import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import type { HealthDailyResponse } from "@/lib/types";
import {
  ROUTE_CATALOG_VERSION,
  createShareSummary,
  createEmptyQuestState,
  createLedgerEntry,
  createQuestExportEnvelope,
  deriveQuestProgress,
  displayQuestAssetPath,
  getRouteByVersion,
  createBrowserQuestRepository,
  latestLedgerEntries,
  renderShareCardSvg,
  routeCatalog,
  routeDistanceMeters,
  routeLandmarks,
  shareFilename,
  shareText,
  upsertDailyLedgerEntry,
  verifyQuestExportEnvelope,
  type QuestExportEnvelope,
  type QuestProgress,
  type QuestRoute,
  type QuestState,
  type RouteAssetRef,
  type RouteLandmark,
  type RouteReward,
  type ShareSummary,
} from "@/quest";
import { fmtDurationMinutes, fmtMiles, fmtNumber } from "@/lib/format";

interface QuestProps {
  healthDaily: HealthDailyResponse;
}

const APP_VERSION = "0.1.0";

type MeteredLandmark = RouteLandmark & { meter: number };

interface UnlockNotice {
  routeTitle: string;
  landmarks: string[];
  relics: string[];
  badges: string[];
}

interface RouteMapPoint {
  landmark: MeteredLandmark;
  x: number;
  y: number;
  coordinateBacked: boolean;
}

interface RoutePathPoint {
  meter: number;
  x: number;
  y: number;
}

interface RouteMapViewport {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  coordinateBacked: boolean;
}

export default function Quest({ healthDaily }: QuestProps) {
  const repository = useMemo(() => createBrowserQuestRepository(), []);
  const [state, setState] = useState<QuestState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [unlockNotice, setUnlockNotice] = useState<UnlockNotice | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    repository.load().then((saved) => {
      if (!active) return;
      setState(
        saved ??
          createEmptyQuestState(ROUTE_CATALOG_VERSION, new Date().toISOString()),
      );
    });
    return () => {
      active = false;
    };
  }, [repository]);

  const activeRoute = state?.activeQuest
    ? getRouteByVersion(state.activeQuest.routeVersionId)
    : null;
  const progress =
    state && activeRoute ? deriveQuestProgress(activeRoute, state) : null;
  const latestRows = healthDaily.rows.slice(-7);

  useEffect(() => {
    if (!state?.activeQuest || !activeRoute) return;
    const next = syncHealthRows(state, activeRoute, healthDaily);
    if (next !== state) {
      const highlights = questUnlockHighlights(activeRoute, state, next);
      if (highlights) setUnlockNotice(highlights);
      setState(next);
      void repository.save(next);
    }
  }, [activeRoute, healthDaily, repository, state]);

  const startQuest = useCallback(
    async (route: QuestRoute, mode: "today" | "backfill") => {
      const switching =
        state?.activeQuest &&
        state.activeQuest.routeVersionId !== route.routeVersionId;
      if (
        state?.activeQuest?.routeVersionId === route.routeVersionId &&
        mode === "today"
      ) {
        setNotice(`${route.title} is already active.`);
        return;
      }
      if (switching) {
        const ok = window.confirm(switchConfirmMessage(route, mode));
        if (!ok) return;
      }
      const rows =
        mode === "today" ? healthDaily.rows.slice(-1) : healthDaily.rows;
      const startDate = rows[0]?.date ?? healthDaily.endDate;
      const now = new Date().toISOString();
      let next =
        state ??
        createEmptyQuestState(ROUTE_CATALOG_VERSION, new Date().toISOString());
      next = {
        ...next,
        activeQuest: {
          routeId: route.routeId,
          routeVersionId: route.routeVersionId,
          startedAt: now,
          startDate,
        },
        updatedAt: now,
      };
      next = syncHealthRows(next, route, {
        ...healthDaily,
        rows,
      });
      setState(next);
      await repository.save(next);
      setNotice(
        mode === "today"
          ? `${route.title} is active from today.`
          : `${route.title} applied your latest 28 days.`,
      );
    },
    [healthDaily, repository, state],
  );

  const resetQuest = useCallback(async () => {
    if (!window.confirm("Reset local quest progress?")) return;
    const next = createEmptyQuestState(
      ROUTE_CATALOG_VERSION,
      new Date().toISOString(),
    );
    setState(next);
    await repository.save(next);
    setNotice("Local quest progress reset.");
  }, [repository]);

  const openChests = useCallback(
    async (rewardIds: string[]) => {
      if (!state) return;
      const newRewardIds = rewardIds.filter(
        (rewardId) => !state.openedChestIds.includes(rewardId),
      );
      if (newRewardIds.length === 0) return;
      const next = {
        ...state,
        openedChestIds: [...state.openedChestIds, ...newRewardIds],
        updatedAt: new Date().toISOString(),
      };
      setState(next);
      await repository.save(next);
      setNotice(
        newRewardIds.length === 1
          ? "Relic chest opened and saved."
          : `${newRewardIds.length} relic chests opened and saved.`,
      );
    },
    [repository, state],
  );

  const exportQuest = useCallback(async () => {
    if (!state) return;
    const envelope = await createQuestExportEnvelope({
      appVersion: APP_VERSION,
      exportedAt: new Date().toISOString(),
      routeCatalogVersion: ROUTE_CATALOG_VERSION,
      payload: state,
    });
    const blob = new Blob([JSON.stringify(envelope, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `fitness-rpg-quest-${dateStamp()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setNotice("Quest progress exported.");
  }, [state]);

  const importQuest = useCallback(
    async (file: File) => {
      const text = await file.text();
      const envelope = JSON.parse(text) as QuestExportEnvelope;
      const valid = await verifyQuestExportEnvelope(envelope);
      const warnings = [
        valid ? null : "checksum mismatch",
        envelope.routeCatalogVersion === ROUTE_CATALOG_VERSION
          ? null
          : "route catalog version mismatch",
      ].filter(Boolean);
      const message =
        warnings.length > 0
          ? `Import has ${warnings.join(" and ")}. Replace local progress anyway?`
          : "Import quest progress and replace local progress?";
      if (!window.confirm(message)) return;
      setState(envelope.payload);
      await repository.save(envelope.payload);
      setNotice(valid ? "Quest progress imported." : "Imported with warnings.");
    },
    [repository],
  );

  const shareBadge = useCallback(async () => {
    if (!progress || !activeRoute) return;
    const badge = latestBadgeForRoute(state, activeRoute);
    const summary = createShareSummary({
      kind: "badge_earned",
      route: activeRoute,
      progress,
      badgeTitle: badge?.title ?? `${activeRoute.title} badge earned`,
    });
    const text = shareText(summary);
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: summary.title, text, url });
        setNotice("Badge shared.");
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
      }
    }
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${text}\n${url}`).catch(() => {});
    }
    downloadShareCard(summary);
    setNotice("Share card downloaded and share text copied.");
  }, [activeRoute, progress, state]);

  if (!state) {
    return (
      <section className="card p-5">
        <div className="skeleton h-28 rounded-lg" />
      </section>
    );
  }

  if (!activeRoute || !progress) {
    return (
      <div className="space-y-6">
        <RoutePicker
          state={state}
          notice={notice}
          fileInputRef={fileInputRef}
          onImport={(file) => void importQuest(file)}
          onStart={(route, mode) => void startQuest(route, mode)}
        />
      </div>
    );
  }

  const nextMeters = progress.nextLandmark
    ? metersToLandmark(activeRoute, progress.nextLandmark, progress.meters)
    : 0;
  const today = healthDaily.rows[healthDaily.rows.length - 1];
  const routeBadges = state.earnedBadges.filter(
    (badge) => badge.routeVersionId === activeRoute.routeVersionId,
  );

  return (
    <div className="space-y-6">
      <section className="card overflow-hidden">
        <RouteHero route={activeRoute} />
        <div className="p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-medium text-accent">Active quest</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight">
                {activeRoute.title}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600 dark:text-zinc-400">
                {activeRoute.description}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center sm:min-w-[360px]">
              <QuestStat label="Level" value={fmtNumber(progress.level)} />
              <QuestStat label="XP" value={fmtNumber(progress.xp)} />
              <QuestStat
                label="Complete"
                value={`${Math.round(progress.percentComplete)}%`}
              />
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-medium">
                {progress.nextLandmark
                  ? `Next: ${progress.nextLandmark.title}`
                  : `${activeRoute.title} complete`}
              </span>
              <span className="text-zinc-500">
                {progress.nextLandmark
                  ? `${fmtNumber(Math.ceil(nextMeters))} m to go`
                  : "Badge earned"}
              </span>
            </div>
            <QuestProgressBar route={activeRoute} progress={progress} />
          </div>
        </div>
      </section>

      <JourneyPanel route={activeRoute} progress={progress} />

      <section className="card p-5">
        <h2 className="text-lg font-semibold">Today on the quest</h2>
        {today ? (
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <QuestStat label="Steps" value={fmtNumber(today.steps)} />
            <QuestStat
              label="Zone min"
              value={fmtNumber(today.activeZoneMinutes.total)}
            />
            <QuestStat
              label="Sleep"
              value={fmtDurationMinutes(today.sleep?.minutesAsleep ?? 0)}
            />
            <QuestStat
              label="Rested bonus"
              value={today.sleep && today.sleep.minutesAsleep >= 420 ? "Yes" : "—"}
            />
          </div>
        ) : (
          <p className="mt-2 text-sm text-zinc-500">No health row for today.</p>
        )}
      </section>

      <section className="card p-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold">Relics & badges</h2>
            <p className="mt-1 text-sm text-zinc-500">
              Sights, relic chests, and badges unlock from {activeRoute.title} progress.
            </p>
          </div>
          {state.earnedBadges.length > 0 && (
            <span className="chip bg-accent-soft text-accent">
              {state.earnedBadges.length} total badges earned
            </span>
          )}
        </div>
        <RewardGrid
          route={activeRoute}
          progress={progress}
          state={state}
          onOpenChest={(rewardId) => void openChests([rewardId])}
          onOpenChests={(rewardIds) => void openChests(rewardIds)}
        />
        {routeBadges.length > 0 && (
          <div className="mt-4 rounded-xl border border-accent/30 bg-accent-soft p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <RewardAsset
                  assetId={completionBadgeAssetId(activeRoute)}
                  title={routeBadges.at(-1)?.title ?? `${activeRoute.title} badge`}
                />
                <p className="text-sm font-semibold text-accent">
                  {routeBadges.at(-1)?.title}
                </p>
                <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">
                  {activeRoute.title} badge saved locally.
                </p>
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void shareBadge()}
              >
                Share badge
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="card p-5">
        <QuestLogPanel
          route={activeRoute}
          state={state}
          rows={latestRows}
          onShowUnlockNotice={setUnlockNotice}
        />
      </section>

      <section className="card p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Progress backup</h2>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-outline"
              onClick={() => void exportQuest()}
            >
              Export
            </button>
            <ImportButton
              fileInputRef={fileInputRef}
              onImport={(file) => void importQuest(file)}
            />
          </div>
        </div>
        <p className="mt-3 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
          Backups are readable JSON with a checksum. The checksum catches
          accidental edits and casual tampering, but it is not anti-cheat.
        </p>
        <button
          type="button"
          className="btn-ghost mt-4"
          onClick={() => void resetQuest()}
        >
          Reset local quest
        </button>
        {notice && <p className="mt-3 text-sm text-zinc-500">{notice}</p>}
      </section>

      <RoutePicker
        compact
        state={state}
        notice={notice}
        fileInputRef={fileInputRef}
        onImport={(file) => void importQuest(file)}
        onStart={(route, mode) => void startQuest(route, mode)}
      />
      {unlockNotice ? (
        <UnlockNoticeCard
          notice={unlockNotice}
          onDismiss={() => setUnlockNotice(null)}
        />
      ) : null}
    </div>
  );
}

function RoutePicker({
  compact = false,
  state,
  notice,
  fileInputRef,
  onImport,
  onStart,
}: {
  compact?: boolean;
  state: QuestState;
  notice: string | null;
  fileInputRef: RefObject<HTMLInputElement>;
  onImport: (file: File) => void;
  onStart: (route: QuestRoute, mode: "today" | "backfill") => void;
}) {
  const pickerBody = (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-medium text-accent">Quest</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {compact ? "Switch quest" : "Start a quest"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600 dark:text-zinc-400">
            Pick a route, then apply today or your latest 28 days. Previous
            route progress, chests, and badges stay saved locally.
          </p>
        </div>
        <ImportButton fileInputRef={fileInputRef} onImport={onImport} />
      </div>
      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {routeCatalog.map((route) => {
          const selected =
            state.activeQuest?.routeVersionId === route.routeVersionId;
          const routeProgress = deriveQuestProgress(route, state);
          return (
            <div
              key={route.routeVersionId}
              className={`rounded-xl border p-4 ${
                selected
                  ? "border-accent bg-accent-soft"
                  : "border-zinc-200 dark:border-zinc-800"
              }`}
            >
              <div className="flex gap-3">
                <RouteThumbnail route={route} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{route.title}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-zinc-500">
                    {route.description}
                  </p>
                </div>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                <div
                  className="h-full rounded-full bg-accent"
                  style={{ width: `${routeProgress.percentComplete}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-zinc-500">
                {Math.round(routeProgress.percentComplete)}% complete ·{" "}
                {openedChestCount(route, state)} relics opened
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={selected}
                  className={selected ? "btn-primary" : "btn-outline"}
                  onClick={() => onStart(route, "today")}
                >
                  {selected
                    ? "Active"
                    : state.activeQuest
                      ? "Make active"
                      : "Start today"}
                </button>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => onStart(route, "backfill")}
                >
                  {state.activeQuest && !selected ? "Apply 28 days" : "Use 28 days"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {notice && <p className="mt-3 text-sm text-zinc-500">{notice}</p>}
    </>
  );

  if (compact) {
    return (
      <details className="card overflow-hidden">
        <summary className="cursor-pointer list-none p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-accent">Route shelf</p>
              <h2 className="mt-1 text-lg font-semibold">Switch or start another quest</h2>
              <p className="mt-1 text-sm text-zinc-500">
                Collapsed while a quest is active so the current journey stays first.
              </p>
            </div>
            <span className="btn-outline self-start">Open routes</span>
          </div>
        </summary>
        <div className="border-t border-zinc-200 p-5 dark:border-zinc-800">
          {pickerBody}
        </div>
      </details>
    );
  }

  return (
    <section className="card overflow-hidden">
      {!compact && <RouteHero route={routeCatalog[0]} />}
      <div className="p-5">
        {pickerBody}
      </div>
    </section>
  );
}

function switchConfirmMessage(
  route: QuestRoute,
  mode: "today" | "backfill",
): string {
  if (mode === "backfill") {
    return `Apply your latest 28 days to ${route.title}? Existing route progress, opened chests, and badges stay saved on their current routes.`;
  }
  return `Make ${route.title} active from today? Existing route progress, opened chests, and badges stay saved on their current routes.`;
}

function JourneyPanel({
  route,
  progress,
}: {
  route: QuestRoute;
  progress: QuestProgress;
}) {
  const [selectedLandmarkId, setSelectedLandmarkId] = useState<string | null>(
    progress.nextLandmark?.landmarkId ?? null,
  );
  const landmarks = routeLandmarks(route)
    .map((landmark) => ({
      ...landmark,
      meter: landmarkMeter(route, landmark),
    }))
    .sort((a, b) => a.meter - b.meter);
  const reachedCount = landmarks.filter(
    (landmark) => landmark.meter <= progress.meters,
  ).length;
  const totalDistance = routeDistanceMeters(route);
  const selectedLandmark =
    landmarks.find((landmark) => landmark.landmarkId === selectedLandmarkId) ??
    landmarks.find(
      (landmark) => landmark.landmarkId === progress.nextLandmark?.landmarkId,
    ) ??
    landmarks[Math.max(0, reachedCount - 1)] ??
    landmarks[0];
  const distanceLabel =
    route.displayDistanceMode === "scaled_distance"
      ? "scaled route"
      : route.displayDistanceMode.replaceAll("_", " ");
  const rawMapPoints = landmarks.map((landmark, index) => ({
    landmark,
    ...rawMapPointForLandmark(landmark, index, landmarks.length, totalDistance),
  }));
  const mapViewport = mapViewportForPoints(rawMapPoints);
  const mapPoints = rawMapPoints.map((point) =>
    normalizeMapPoint(point, mapViewport),
  );
  const routePathPoints = routePathPointsForMap(mapPoints);
  const pathSegments = pathSegmentsForPoints(routePathPoints);
  const currentPoint = currentRoutePoint(routePathPoints, progress.meters);
  const progressPathSegments = pathSegmentsForPoints(
    progressPathPoints(routePathPoints, currentPoint, progress.meters),
  );
  const denseWorldMap =
    mapViewport.coordinateBacked &&
    mapViewport.minX === 0 &&
    mapViewport.maxX === 100 &&
    mapViewport.minY === 0 &&
    mapViewport.maxY === 100 &&
    mapPoints.length > 20;
  const mapLayoutClass = denseWorldMap
    ? "mt-5 grid gap-4"
    : "mt-5 grid gap-4 lg:grid-cols-[1.35fr_0.65fr]";
  const mapFrameClass = denseWorldMap
    ? "relative min-h-[430px] overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-950 dark:border-zinc-800 lg:min-h-[540px]"
    : "relative min-h-[360px] overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-950 dark:border-zinc-800";

  return (
    <section className="card p-5" aria-label={`${route.title} journey map`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-medium text-accent">Journey map</p>
          <h2 className="mt-1 text-lg font-semibold">{route.title} route</h2>
          <p className="mt-1 text-sm text-zinc-500">
            {fmtNumber(reachedCount)} of {fmtNumber(landmarks.length)} stops reached ·{" "}
            {distanceLabel}
          </p>
        </div>
        <div className="text-sm text-zinc-500">
          {progress.nextLandmark ? (
            <>
              Next stop{" "}
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                {progress.nextLandmark.title}
              </span>
            </>
          ) : (
            <span className="font-medium text-accent">Route complete</span>
          )}
        </div>
      </div>

      <div className={mapLayoutClass}>
        <div className={mapFrameClass}>
          <RouteMapBackdrop route={route} viewport={mapViewport} />
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            role="img"
            aria-label={`${route.title} route path`}
            preserveAspectRatio="none"
          >
            {pathSegments.map((pathD, index) => (
              <path
                key={`route-${index}`}
                d={pathD}
                fill="none"
                stroke="rgba(255,255,255,0.34)"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
            {progressPathSegments.map((pathD, index) => (
              <path
                key={`progress-${index}`}
                d={pathD}
                fill="none"
                stroke="rgb(34,211,238)"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
          </svg>
          <div
            data-testid="quest-current-position"
            className="absolute z-30 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-cyan-300 shadow-[0_0_22px_rgba(34,211,238,0.9)]"
            style={{
              left: `${currentPoint.x}%`,
              top: `${currentPoint.y}%`,
            }}
            aria-label="Current route position"
          />
          <div
            className="pointer-events-none absolute z-30 -translate-y-1/2 rounded-full border border-cyan-100/70 bg-cyan-950/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-100 shadow-lg backdrop-blur"
            style={{
              left: `${Math.min(92, currentPoint.x + 2.8)}%`,
              top: `${currentPoint.y}%`,
            }}
          >
            You
          </div>
          {mapPoints.map(({ landmark, x, y }) => {
            const reached = landmark.meter <= progress.meters;
            const next =
              progress.nextLandmark?.landmarkId === landmark.landmarkId;
            const selected = selectedLandmark?.landmarkId === landmark.landmarkId;
            return (
              <button
                key={landmark.landmarkId}
                type="button"
                data-landmark-id={landmark.landmarkId}
                className={`absolute ${
                  denseWorldMap ? "h-4 w-4 border" : "h-5 w-5 border-2"
                } -translate-x-1/2 -translate-y-1/2 rounded-full text-[0px] shadow-lg transition-transform hover:scale-125 focus:outline-none focus:ring-2 focus:ring-amber-200 ${
                  selected
                    ? "z-20 scale-125 border-amber-100 bg-amber-400 shadow-[0_0_18px_rgba(251,191,36,0.85)]"
                    : reached
                      ? "z-10 border-white bg-accent"
                      : next
                        ? "z-10 border-cyan-100 bg-cyan-950 shadow-[0_0_14px_rgba(34,211,238,0.65)]"
                        : "z-10 border-white/70 bg-zinc-800"
                }`}
                style={{ left: `${x}%`, top: `${y}%` }}
                onClick={() => setSelectedLandmarkId(landmark.landmarkId)}
                onFocus={() => setSelectedLandmarkId(landmark.landmarkId)}
                onMouseEnter={() => setSelectedLandmarkId(landmark.landmarkId)}
              >
                {landmark.title}
              </button>
            );
          })}
          <div className="absolute bottom-3 left-3 rounded-lg bg-black/55 px-3 py-2 text-xs text-white backdrop-blur">
            {questDistanceLabel(progress.meters)} walked ·{" "}
            {Math.round(progress.percentComplete)}%
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-200 p-4 dark:border-zinc-800">
          {selectedLandmark ? (
            <>
              <LandmarkAsset landmark={selectedLandmark} />
              <p className="text-xs font-medium uppercase tracking-wider text-accent">
                {selectedLandmark.meter <= progress.meters
                  ? "Reached stop"
                  : progress.nextLandmark?.landmarkId === selectedLandmark.landmarkId
                    ? "Next stop"
                    : "Upcoming stop"}
              </p>
              <h3 className="mt-1 text-lg font-semibold">
                {selectedLandmark.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
                {selectedLandmark.description}
              </p>
              {selectedLandmark.url ? (
                <a
                  href={selectedLandmark.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex text-sm font-medium text-accent hover:underline"
                >
                  Learn about this stop
                </a>
              ) : null}
              <p className="mt-3 text-xs text-zinc-500">
                {totalDistance > 0
                  ? `${Math.round((selectedLandmark.meter / totalDistance) * 100)}% along the route`
                  : "Route stop"}
              </p>
            </>
          ) : (
            <p className="text-sm text-zinc-500">No route stops available.</p>
          )}
        </div>
      </div>
    </section>
  );
}

function RouteMapBackdrop({
  route,
  viewport,
}: {
  route: QuestRoute;
  viewport: RouteMapViewport;
}) {
  const mapAssetId = route.assets.find((asset) => asset.kind === "map")?.assetId;
  const map = mapAssetId ? assetPath(mapAssetId) : null;
  const hero =
    map ??
    (route.shareMeta.ogImageAssetId
      ? assetPath(route.shareMeta.ogImageAssetId)
      : routeAssetPath(route, "hero"));
  return (
    <>
      {hero ? (
        <img
          src={hero}
          alt={map ? `${route.title} route map` : `${route.title} route artwork`}
          loading="lazy"
          decoding="async"
          className={`absolute ${
            map ? "object-fill opacity-75" : "object-cover opacity-45"
          }`}
          style={
            map && viewport.coordinateBacked
              ? mapCropStyle(viewport)
              : { inset: 0, height: "100%", width: "100%" }
          }
        />
      ) : null}
      <div
        className={
          map
            ? "absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.22),rgba(2,6,23,0.56))]"
            : "absolute inset-0 bg-[linear-gradient(135deg,rgba(8,47,73,0.86),rgba(15,23,42,0.8)),radial-gradient(circle_at_25%_25%,rgba(34,211,238,0.28),transparent_24%),radial-gradient(circle_at_80%_65%,rgba(20,184,166,0.18),transparent_24%)]"
        }
      />
      <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.18)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.18)_1px,transparent_1px)] [background-size:48px_48px]" />
    </>
  );
}

function LandmarkAsset({ landmark }: { landmark: MeteredLandmark }) {
  const path = landmark.assetId ? assetPath(landmark.assetId) : null;
  if (!path) return null;
  return (
    <div className="mb-4 overflow-hidden rounded-xl bg-zinc-100 dark:bg-zinc-950">
      <img
        src={path}
        alt={`${landmark.title} sight`}
        loading="lazy"
        decoding="async"
        className="h-40 w-full object-cover"
      />
    </div>
  );
}

function rawMapPointForLandmark(
  landmark: MeteredLandmark,
  index: number,
  totalStops: number,
  totalDistance: number,
): Omit<RouteMapPoint, "landmark"> {
  if (landmark.coordinates) {
    const lon = Math.min(180, Math.max(-180, landmark.coordinates.lon));
    const lat = Math.min(90, Math.max(-90, landmark.coordinates.lat));
    return {
      x: ((lon + 180) / 360) * 100,
      y: ((90 - lat) / 180) * 100,
      coordinateBacked: true,
    };
  }
  const rawPercent =
    totalDistance > 0
      ? (landmark.meter / totalDistance) * 100
      : totalStops <= 1
        ? 50
        : (index / (totalStops - 1)) * 100;
  const x = Math.min(94, Math.max(6, rawPercent));
  const wave = Math.sin(index * 1.35) * 18;
  const drift = index % 2 === 0 ? -7 : 7;
  const y = Math.min(84, Math.max(16, 50 + wave + drift));
  return { x, y, coordinateBacked: false };
}

function mapViewportForPoints(points: RouteMapPoint[]): RouteMapViewport {
  const coordinatePoints = points.filter((point) => point.coordinateBacked);
  if (coordinatePoints.length < 2) {
    return { minX: 0, maxX: 100, minY: 0, maxY: 100, coordinateBacked: false };
  }

  const xs = coordinatePoints.map((point) => point.x);
  const ys = coordinatePoints.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  if (maxX - minX > 70 || maxY - minY > 60) {
    return { minX: 0, maxX: 100, minY: 0, maxY: 100, coordinateBacked: true };
  }
  return paddedViewport(minX, maxX, minY, maxY);
}

function paddedViewport(
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
): RouteMapViewport {
  const paddedX = expandRange(minX, maxX, 8, 34, 0, 100);
  const paddedY = expandRange(minY, maxY, 8, 48, 0, 100);
  return {
    minX: paddedX.min,
    maxX: paddedX.max,
    minY: paddedY.min,
    maxY: paddedY.max,
    coordinateBacked: true,
  };
}

function expandRange(
  min: number,
  max: number,
  padding: number,
  minimumSpan: number,
  lowerBound: number,
  upperBound: number,
): { min: number; max: number } {
  let nextMin = min - padding;
  let nextMax = max + padding;
  const span = nextMax - nextMin;
  if (span < minimumSpan) {
    const deficit = minimumSpan - span;
    nextMin -= deficit / 2;
    nextMax += deficit / 2;
  }
  if (nextMin < lowerBound) {
    nextMax += lowerBound - nextMin;
    nextMin = lowerBound;
  }
  if (nextMax > upperBound) {
    nextMin -= nextMax - upperBound;
    nextMax = upperBound;
  }
  return {
    min: Math.max(lowerBound, nextMin),
    max: Math.min(upperBound, nextMax),
  };
}

function normalizeMapPoint(
  point: RouteMapPoint,
  viewport: RouteMapViewport,
): RouteMapPoint {
  if (!viewport.coordinateBacked) return point;
  const width = viewport.maxX - viewport.minX || 1;
  const height = viewport.maxY - viewport.minY || 1;
  return {
    ...point,
    x: ((point.x - viewport.minX) / width) * 100,
    y: ((point.y - viewport.minY) / height) * 100,
  };
}

function routePathPointsForMap(points: RouteMapPoint[]): RoutePathPoint[] {
  const sorted = [...points].sort((a, b) => a.landmark.meter - b.landmark.meter);
  const pathPoints = sorted.map((point) => ({
    meter: point.landmark.meter,
    x: point.x,
    y: point.y,
  }));
  const first = pathPoints[0];
  if (!first || first.meter <= 0) return pathPoints;
  return [routeStartPoint(pathPoints), ...pathPoints];
}

function routeStartPoint(points: RoutePathPoint[]): RoutePathPoint {
  const first = points[0];
  const next = points[1];
  if (!next) {
    return {
      meter: 0,
      x: clamp(first.x - 16, 4, 96),
      y: first.y,
    };
  }

  const dx = next.x - first.x;
  const dy = next.y - first.y;
  const distance = Math.hypot(dx, dy) || 1;
  const offset = clamp(distance * 0.55, 14, 24);
  return {
    meter: 0,
    x: clamp(first.x - (dx / distance) * offset, 4, 96),
    y: clamp(first.y - (dy / distance) * offset, 8, 92),
  };
}

function currentRoutePoint(
  points: RoutePathPoint[],
  progressMeters: number,
): { x: number; y: number } {
  if (points.length === 0) return { x: 50, y: 50 };
  const sorted = [...points].sort((a, b) => a.meter - b.meter);
  if (progressMeters <= sorted[0].meter) {
    return { x: sorted[0].x, y: sorted[0].y };
  }
  for (let index = 1; index < sorted.length; index += 1) {
    const prev = sorted[index - 1];
    const next = sorted[index];
    if (progressMeters <= next.meter) {
      const span = next.meter - prev.meter || 1;
      const t = Math.min(1, Math.max(0, (progressMeters - prev.meter) / span));
      const nextX =
        Math.abs(next.x - prev.x) > 50
          ? next.x > prev.x
            ? next.x - 100
            : next.x + 100
          : next.x;
      const wrappedX = prev.x + (nextX - prev.x) * t;
      return {
        x: ((wrappedX % 100) + 100) % 100,
        y: prev.y + (next.y - prev.y) * t,
      };
    }
  }
  const last = sorted[sorted.length - 1];
  return { x: last.x, y: last.y };
}

function progressPathPoints(
  points: RoutePathPoint[],
  currentPoint: { x: number; y: number },
  progressMeters: number,
): RoutePathPoint[] {
  if (points.length === 0) return [];
  const sorted = [...points].sort((a, b) => a.meter - b.meter);
  if (progressMeters <= sorted[0].meter) return [];
  const reached = sorted.filter((point) => point.meter <= progressMeters);
  const pathPoints = reached.map((point) => ({ ...point }));
  const lastReached = pathPoints[pathPoints.length - 1];
  const shouldAppendCurrent =
    !lastReached ||
    Math.abs(lastReached.x - currentPoint.x) > 0.01 ||
    Math.abs(lastReached.y - currentPoint.y) > 0.01;
  return shouldAppendCurrent
    ? [...pathPoints, { ...currentPoint, meter: progressMeters }]
    : pathPoints;
}

function pathSegmentsForPoints(points: RoutePathPoint[]): string[] {
  if (points.length === 0) return [];
  const sorted = [...points].sort((a, b) => a.meter - b.meter);
  const segments: RoutePathPoint[][] = [];
  let current: RoutePathPoint[] = [];
  for (const point of sorted) {
    const previous = current.at(-1);
    if (previous && Math.abs(point.x - previous.x) > 50) {
      if (current.length > 1) segments.push(current);
      current = [point];
    } else {
      current.push(point);
    }
  }
  if (current.length > 1) segments.push(current);
  return segments.map((segment) =>
    segment
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" "),
  );
}

function mapCropStyle(viewport: RouteMapViewport): CSSProperties {
  const width = viewport.maxX - viewport.minX || 100;
  const height = viewport.maxY - viewport.minY || 100;
  return {
    height: `${10000 / height}%`,
    left: `${(-viewport.minX * 100) / width}%`,
    maxWidth: "none",
    top: `${(-viewport.minY * 100) / height}%`,
    width: `${10000 / width}%`,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function questDistanceLabel(meters: number): string {
  if (!Number.isFinite(meters)) return "—";
  if (meters < 160.9344) return `${fmtNumber(meters * 3.28084)} ft`;
  return `${fmtMiles(meters)} mi`;
}

function QuestLogPanel({
  route,
  state,
  rows,
  onShowUnlockNotice,
}: {
  route: QuestRoute;
  state: QuestState;
  rows: HealthDailyResponse["rows"];
  onShowUnlockNotice: (notice: UnlockNotice) => void;
}) {
  const entries = latestLedgerEntries(state.ledger).filter(
    (entry) => entry.assignedRouteVersionId === route.routeVersionId,
  );
  const landmarks = routeLandmarks(route)
    .map((landmark) => ({ ...landmark, meter: landmarkMeter(route, landmark) }))
    .sort((a, b) => a.meter - b.meter);
  const logRows = rows
    .map((row) => {
      const dayEntry = entries.find((entry) => entry.sourceDate === row.date);
      return { dayEntry, row };
    })
    .filter(({ dayEntry }) => (dayEntry?.meters ?? 0) > 0)
    .sort((a, b) => b.row.date.localeCompare(a.row.date));

  return (
    <>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Quest log</h2>
          <p className="mt-1 text-sm text-zinc-500">
            A positive quest journal from recent movement, sleep, and route progress.
          </p>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {logRows.length === 0 ? (
          <div className="rounded-xl border border-zinc-200 bg-white/70 p-4 dark:border-zinc-800 dark:bg-zinc-950/40">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              No quest movement has been logged yet. Walk, sync, or add manual miles to start writing this route's journal.
            </p>
          </div>
        ) : null}
        {logRows.map(({ dayEntry, row }) => {
          const metersThroughDay = sumMetersThroughDate(entries, row.date);
          const metersBeforeDay = sumMetersBeforeDate(entries, row.date);
          const currentStop =
            [...landmarks]
              .reverse()
              .find((landmark) => landmark.meter <= metersThroughDay) ??
            landmarks[0];
          const nextStop =
            landmarks.find((landmark) => landmark.meter > metersThroughDay) ??
            null;
          const title = currentStop
            ? `${row.date} · ${currentStop.title}`
            : row.date;
          const milestoneNotice = unlockNoticeForProgressWindow(
            route,
            metersBeforeDay,
            metersThroughDay,
          );
          const highlighted = Boolean(milestoneNotice?.landmarks.length);
          const body = (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs font-medium text-zinc-500">{title}</p>
                {highlighted ? (
                  <span className="chip w-fit bg-accent-soft text-accent">
                    Waypoint reached
                  </span>
                ) : null}
              </div>
              <p className="mt-2 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
                {creativeQuestLogLine({
                  row,
                  route,
                  dayMeters: dayEntry?.meters ?? 0,
                  currentStop,
                  nextStop,
                })}
              </p>
            </>
          );
          if (milestoneNotice) {
            return (
              <button
                key={row.date}
                type="button"
                className={`w-full rounded-xl border p-4 text-left transition hover:border-accent/60 hover:bg-accent-soft/40 ${
                  highlighted
                    ? "border-accent/40 bg-accent-soft/30"
                    : "border-zinc-200 bg-white/70 dark:border-zinc-800 dark:bg-zinc-950/40"
                }`}
                onClick={() => onShowUnlockNotice(milestoneNotice)}
              >
                {body}
              </button>
            );
          }
          return (
            <article
              key={row.date}
              className="rounded-xl border border-zinc-200 bg-white/70 p-4 dark:border-zinc-800 dark:bg-zinc-950/40"
            >
              {body}
            </article>
          );
        })}
      </div>
    </>
  );
}

function creativeQuestLogLine({
  row,
  route,
  dayMeters,
  currentStop,
  nextStop,
}: {
  row: HealthDailyResponse["rows"][number];
  route: QuestRoute;
  dayMeters: number;
  currentStop?: RouteLandmark;
  nextStop: RouteLandmark | null;
}): string {
  const place = currentStop?.title ?? route.title;
  const next = nextStop?.title ?? "the finish marker";
  const sleep = row.sleep?.minutesAsleep ?? 0;
  const lively = row.activeZoneMinutes.total >= 45 || row.steps >= 10_000;
  const bigDay = dayMeters >= 8_000 || row.steps >= 14_000;
  const rested = sleep >= 420;
  const seed = `${route.routeId}:${row.date}:${place}:${next}:${Math.round(dayMeters)}`;

  if (route.routeId === "under-the-sea") {
    return underTheSeaQuestLogLine({
      currentStop,
      place,
      next,
      seed,
      dayMeters,
      lively,
      bigDay,
      rested,
    });
  }

  const theme = questLogTheme(route.routeId);
  const companion = chooseLogLine(`${seed}:companion`, theme.companions);
  const detail = chooseLogLine(`${seed}:detail`, theme.details);
  const token = chooseLogLine(`${seed}:token`, theme.tokens);
  const pace = questDistanceLabel(dayMeters);

  if (route.routeId === "four-moons-of-jupiter") {
    const world = fourMoonsWorldForStop(place);
    return routeThemedQuestLogLine({
      seed,
      place,
      next,
      pace,
      rested,
      bigDay,
      lively,
      companion,
      detail,
      big: [
        `A strong ${world} traverse carried you ${pace} from ${place}. ${companion} caught the route light while ${detail} made the waypoint feel properly otherworldly.`,
        `You crossed ${world} terrain with real momentum, pocketed ${token}, and left ${place} with ${next} glowing on the transfer chart.`,
        `The day's steps turned into a clean ${world} chapter: ${pace}, a bright marker behind you, and ${companion} keeping the grand tour cheerful.`,
      ],
      steady: [
        `You added ${pace} to the ${world} route near ${place}. ${detail} shimmered beside the path, and ${next} felt less like a distant moon note.`,
        `The grand tour moved at a satisfying pace around ${place}; ${companion} marked the trail while you carried ${token} toward ${next}.`,
        `A measured ${world} crossing still counts. You left a tidy line beyond ${place} and kept the next beacon aimed at ${next}.`,
      ],
      restedLines: [
        `Good sleep made the ${world} horizon feel crisp. You moved ${pace} from ${place}, logged ${detail}, and finished with ${next} bright on the chart.`,
        `Rested legs made the low-gravity miles feel smooth. ${companion} drifted beside the marker line while ${place} slipped behind you.`,
      ],
    });
  }

  return routeThemedQuestLogLine({
    seed,
    place,
    next,
    pace,
    rested,
    bigDay,
    lively,
    companion,
    detail,
    big: [
      `You put ${pace} behind you leaving ${place}, enough for ${detail} to feel like a proper scene change. ${companion} marked the moment, and ${next} moved firmly into range.`,
      `A big quest day: ${pace} through ${place}, ${token} tucked into the logbook, and the route toward ${next} looking satisfyingly shorter.`,
      `The route opened up after ${place}. You covered ${pace}, caught ${detail}, and ended with ${companion} pointing the way toward ${next}.`,
      `Today's movement had real momentum. ${place} fell behind by ${pace}, the map gained a bright new line, and ${next} became the obvious next chapter.`,
    ],
    steady: [
      `You added ${pace} near ${place}, enough to make the map feel newly alive. ${companion} appeared around ${detail}, then the route bent toward ${next}.`,
      `A steady quest day carried you ${pace} through ${place}. You logged ${token}, checked the next marker, and kept ${next} in clean view.`,
      `The day's route note is simple but good: ${pace}, ${place} behind you by a little more, and ${detail} making the approach toward ${next} memorable.`,
      `You kept a useful rhythm around ${place}. ${companion} made a brief appearance, ${token} went into the pack, and ${next} edged closer.`,
    ],
    restedLines: [
      `Good sleep gave the day an easy rhythm. You moved ${pace} from ${place}, noticed ${detail}, and kept the line toward ${next} calm and bright.`,
      `Rested progress has a different feel: ${pace} without strain, ${token} safely packed, and ${next} sitting clearly on the route notes.`,
      `Sleep did quiet work in the background. You left ${place} with steadier legs, logged ${pace}, and let ${companion} keep the day's entry playful.`,
    ],
  });
}

function routeThemedQuestLogLine({
  seed,
  place,
  next,
  pace,
  rested,
  bigDay,
  lively,
  companion,
  detail,
  big,
  steady,
  restedLines,
}: {
  seed: string;
  place: string;
  next: string;
  pace: string;
  rested: boolean;
  bigDay: boolean;
  lively: boolean;
  companion: string;
  detail: string;
  big: string[];
  steady: string[];
  restedLines: string[];
}): string {
  if (rested && bigDay) {
    return chooseLogLine(seed, [
      ...big,
      `Good sleep and strong movement made a clean little legend out of ${place}: ${pace}, ${detail}, and ${companion} all pushing the story toward ${next}.`,
    ]);
  }
  if (bigDay || lively) {
    return chooseLogLine(seed, big);
  }
  if (rested) {
    return chooseLogLine(seed, restedLines);
  }
  return chooseLogLine(seed, steady);
}

function questLogTheme(routeId: string): {
  companions: string[];
  details: string[];
  tokens: string[];
} {
  if (routeId === "moon-trek") {
    return {
      companions: ["a tiny rover drone", "a silver dust devil", "a reflected Earthrise", "a quiet telemetry ping", "a glittering trail marker"],
      details: ["crisp crater shadows", "powdery regolith", "a pale ridge line", "the black sky over the horizon", "a bright ray crossing"],
      tokens: ["a moon-dust bead", "a basalt chip", "a tiny star map", "a polished regolith coin", "a crater-rim note"],
    };
  }
  if (routeId === "mars-traverse") {
    return {
      companions: ["a patient survey rover", "a dust-bright route beacon", "a tiny weather mast", "a copper-colored horizon", "a basalt cairn"],
      details: ["layered red rock", "wind-brushed dunes", "a dry channel bed", "thin afternoon light", "a dark volcanic plain"],
      tokens: ["a basalt compass notch", "a red-dust sample", "a canyon sketch", "a rover waypoint tag", "a small hematite glint"],
    };
  }
  if (routeId === "appalachian-trail") {
    return {
      companions: ["a white blaze", "a ridge breeze", "a friendly trail register", "a chickadee in the branches", "a boot-worn stone step"],
      details: ["green tunnel shade", "a blue ridge overlook", "fern-lined trail", "a mossy creek crossing", "sun on old hardwoods"],
      tokens: ["a pressed fern", "a smooth creek stone", "a shelter logbook note", "a white-blaze charm", "a ridge acorn"],
    };
  }
  if (routeId === "pacific-crest-trail") {
    return {
      companions: ["a desert raven", "a granite cairn", "a pine-scented breeze", "a distant Cascade ridge", "a marmot watching from stone"],
      details: ["desert light", "granite slabs", "a cold alpine creek", "volcanic ash underfoot", "wide western sky"],
      tokens: ["a pinecone charm", "a sun-warmed pebble", "a snowmelt note", "a volcanic glass fleck", "a border-lantern spark"],
    };
  }
  if (routeId === "national-parks-grand-tour") {
    return {
      companions: ["a ranger stamp", "a scenic overlook sign", "a trailhead map", "a bright park badge", "a picnic-table route sketch"],
      details: ["a new park vista", "a visitor-center mural", "a winding scenic road", "a quiet overlook", "a fresh chapter stamp"],
      tokens: ["a passport stamp", "a tiny enamel pin", "a folded park map", "a chest key tag", "a commemorative trail coin"],
    };
  }
  if (routeId === "earth-city-tour") {
    return {
      companions: ["a glowing streetlamp", "a station clock", "a mural on the corner", "a skyline silhouette", "a friendly cafe sign"],
      details: ["morning sidewalks", "a riverfront path", "a downtown plaza", "a neighborhood mural", "lights coming on downtown"],
      tokens: ["a transit token", "a postcard", "a city pin", "a tiny route ticket", "a map-fold crease"],
    };
  }
  if (routeId === "four-moons-of-jupiter") {
    return {
      companions: ["a Jovian beacon", "a low-gravity route flag", "a bright transfer gate", "a small survey bot", "Jupiter's banded glow"],
      details: ["frost-bright terrain", "a volcanic plain", "grooved ice", "an old crater field", "the giant planet overhead"],
      tokens: ["a moonstone shard", "a sulfur-bright pebble", "an ice crystal", "a crater-ring charm", "a transfer-gate spark"],
    };
  }
  return {
    companions: ["a route marker", "a friendly signpost", "a bright map pin", "a small trail charm", "a notebook margin sketch"],
    details: ["a clean bend in the path", "a fresh view", "a quiet landmark", "a useful shortcut", "a satisfying map line"],
    tokens: ["a route token", "a tiny keepsake", "a folded map note", "a waypoint charm", "a small badge glint"],
  };
}

function fourMoonsWorldForStop(place: string): string {
  if (place.includes("Europa") || place.includes("Chaos") || place.includes("Ocean")) {
    return "Europa";
  }
  if (place.includes("Ganymede") || place.includes("Galileo") || place.includes("Grooved") || place.includes("Magnetic")) {
    return "Ganymede";
  }
  if (place.includes("Callisto") || place.includes("Valhalla")) {
    return "Callisto";
  }
  return "Io";
}

function underTheSeaQuestLogLine({
  currentStop,
  place,
  next,
  seed,
  dayMeters,
  lively,
  bigDay,
  rested,
}: {
  currentStop?: RouteLandmark;
  place: string;
  next: string;
  seed: string;
  dayMeters: number;
  lively: boolean;
  bigDay: boolean;
  rested: boolean;
}): string {
  const creature = chooseLogLine(
    `${seed}:wildlife`,
    currentStop?.journal?.wildlife ?? ["a bright school of fish"],
  );
  const feature = currentStop?.journal?.feature ?? "open water";
  if (dayMeters <= 0) {
    return chooseLogLine(seed, [
      `You held position near ${place}, letting the ${feature} settle into the day's log. ${creature} drifted by while ${next} waited on the blue edge of the chart.`,
      `The expedition stayed quiet around ${place}. You checked the gauges, watched ${creature} pass through the dim water, and left ${next} marked for the next outing.`,
      `No rush today: the current around ${place} stayed gentle, the map stayed open, and ${creature} kept the route company until ${next}.`,
    ]);
  }
  if (rested && bigDay) {
    return chooseLogLine(seed, [
      `Rested and full of spark, you rode a bright current out of ${place}. ${creature} flashed alongside the route lights, and ${next} looked close enough to call by name.`,
      `Good sleep made the whole ocean feel clearer. You crossed a bold stretch beyond ${place}, skimmed over ${feature}, and left a glowing trail toward ${next}.`,
      `The day had expedition energy: steady movement, clean water, and ${creature} keeping pace as ${place} gave way to the approach toward ${next}.`,
    ]);
  }
  if (bigDay || lively) {
    return chooseLogLine(seed, [
      `You followed a lively current around ${place}, with ${creature} turning the route into a small parade. By the end, the line toward ${next} was much brighter.`,
      `The blue markers pulled you onward from ${place}. ${creature} slipped through the ${feature}, and the expedition gained a satisfying stretch toward ${next}.`,
      `A strong day carried you past ${place}: bubbles, route lights, and ${creature} all pointing the way toward ${next}.`,
    ]);
  }
  if (rested) {
    return chooseLogLine(seed, [
      `A good rest made the water feel calm around ${place}. You moved with the current, watched ${creature} cross the ${feature}, and kept ${next} shining ahead.`,
      `Well-rested and unhurried, you traced the blue line through ${place}. The sea stayed kind, ${creature} kept watch, and ${next} came a little closer.`,
      `Sleep gave the expedition a smooth start. You glided past ${place}, logged the ${feature}, and left a clear marker toward ${next}.`,
    ]);
  }
  return chooseLogLine(seed, [
    `You made steady ocean progress around ${place}, following the route lights as ${creature} moved through the ${feature}. ${next} is still ahead, but the water between feels friendlier now.`,
    `The day added a quiet blue mile near ${place}. ${creature} appeared just long enough to make the log worth rereading, then the route bent gently toward ${next}.`,
    `You kept the expedition moving at ${place}, one measured stretch through ${feature}. ${creature} handled the dramatic entrance; you handled the progress toward ${next}.`,
    `A small current helped carry the route around ${place}. The map gained a little glow, ${creature} made a cameo, and ${next} stayed fixed on the horizon line.`,
  ]);
}

function chooseLogLine(seed: string, lines: string[]): string {
  return lines[hashString(seed) % lines.length] ?? lines[0] ?? "";
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 2246822507) >>> 0;
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 3266489909) >>> 0;
  return (hash ^ (hash >>> 16)) >>> 0;
}

function sumMetersThroughDate(
  entries: { sourceDate: string; meters: number }[],
  date: string,
): number {
  return entries
    .filter((entry) => entry.sourceDate <= date)
    .reduce((total, entry) => total + entry.meters, 0);
}

function sumMetersBeforeDate(
  entries: { sourceDate: string; meters: number }[],
  date: string,
): number {
  return entries
    .filter((entry) => entry.sourceDate < date)
    .reduce((total, entry) => total + entry.meters, 0);
}

function RewardGrid({
  route,
  progress,
  state,
  onOpenChest,
  onOpenChests,
}: {
  route: QuestRoute;
  progress: QuestProgress;
  state: QuestState;
  onOpenChest: (rewardId: string) => void;
  onOpenChests: (rewardIds: string[]) => void;
}) {
  const rewards = route.rewards.filter(
    (reward) => reward.kind === "chest" || reward.kind === "badge",
  );
  if (rewards.length > 12) {
    return (
      <RewardCollection
        route={route}
        rewards={rewards}
        progress={progress}
        state={state}
        onOpenChest={onOpenChest}
        onOpenChests={onOpenChests}
      />
    );
  }
  return (
    <div className="mt-4 grid gap-3 md:grid-cols-3">
      {rewards.map((reward) => {
        const status = rewardStatus(reward, progress, state);
        return (
          <RewardCard
            key={reward.rewardId}
            route={route}
            reward={reward}
            status={status}
            onOpenChest={onOpenChest}
          />
        );
      })}
    </div>
  );
}

function RewardCollection({
  route,
  rewards,
  progress,
  state,
  onOpenChest,
  onOpenChests,
}: {
  route: QuestRoute;
  rewards: RouteReward[];
  progress: QuestProgress;
  state: QuestState;
  onOpenChest: (rewardId: string) => void;
  onOpenChests: (rewardIds: string[]) => void;
}) {
  const chests = rewards.filter((reward) => reward.kind === "chest");
  const badges = rewards.filter((reward) => reward.kind === "badge");
  const readyChests = chests.filter(
    (reward) => rewardStatus(reward, progress, state) === "ready",
  );
  const openedChests = chests.filter(
    (reward) => rewardStatus(reward, progress, state) === "collected",
  );
  const lockedChests = chests.filter(
    (reward) => rewardStatus(reward, progress, state) === "locked",
  );
  const earnedBadges = badges.filter(
    (reward) => rewardStatus(reward, progress, state) === "collected",
  );

  return (
    <div className="mt-4 space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <QuestStat label="Relics" value={`${openedChests.length}/${chests.length}`} />
        <QuestStat label="Ready" value={fmtNumber(readyChests.length)} />
        <QuestStat label="Badges" value={`${earnedBadges.length}/${badges.length}`} />
        <QuestStat label="Upcoming" value={fmtNumber(lockedChests.length)} />
      </div>

      {readyChests.length > 0 && (
        <section className="rounded-xl border border-accent/30 bg-accent-soft p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-accent">Ready relic chests</h3>
              <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                Open the stops your walking has already reached.
              </p>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => onOpenChests(readyChests.map((reward) => reward.rewardId))}
            >
              Open all ready
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {readyChests.slice(0, 6).map((reward) => (
              <RewardCard
                key={reward.rewardId}
                route={route}
                reward={reward}
                status="ready"
                onOpenChest={onOpenChest}
              />
            ))}
          </div>
          {readyChests.length > 6 && (
            <p className="mt-3 text-xs text-zinc-600 dark:text-zinc-400">
              {readyChests.length - 6} more ready relic chests are included in Open all ready.
            </p>
          )}
        </section>
      )}

      <details className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-800" open>
        <summary className="cursor-pointer text-sm font-semibold">
          Badges
        </summary>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {badges.map((reward) => (
            <RewardCard
              key={reward.rewardId}
              route={route}
              reward={reward}
              status={rewardStatus(reward, progress, state)}
              onOpenChest={onOpenChest}
            />
          ))}
        </div>
      </details>

      <details className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-800" open>
        <summary className="cursor-pointer text-sm font-semibold">
          Next stops
        </summary>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {lockedChests.slice(0, 8).map((reward) => (
            <RewardCard
              key={reward.rewardId}
              route={route}
              reward={reward}
              status="locked"
              compact
              onOpenChest={onOpenChest}
            />
          ))}
        </div>
      </details>

      {(openedChests.length > 0 || earnedBadges.length > 0) && (
        <details className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-800">
          <summary className="cursor-pointer text-sm font-semibold">
            Collection log
          </summary>
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            {[...openedChests.slice(-8), ...earnedBadges].map((reward) => (
              <RewardCard
                key={reward.rewardId}
                route={route}
                reward={reward}
                status="collected"
                compact
                onOpenChest={onOpenChest}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function RewardCard({
  route,
  reward,
  status,
  compact = false,
  onOpenChest,
}: {
  route: QuestRoute;
  reward: RouteReward;
  status: RewardStatus;
  compact?: boolean;
  onOpenChest: (rewardId: string) => void;
}) {
  const locked = status === "locked";
  const displayTitle = locked
    ? reward.kind === "badge"
      ? "Locked badge"
      : "Locked relic chest"
    : reward.title;
  const lockedHint = locked ? lockedRewardHint(route, reward) : null;
  return (
    <div
      className={`rounded-xl border p-4 ${
        locked
          ? "border-zinc-200 bg-zinc-50 text-zinc-500 dark:border-zinc-800 dark:bg-zinc-950/50"
          : "border-zinc-200 dark:border-zinc-800"
      }`}
    >
      {!compact && (
        locked ? (
          <LockedRelicAsset title={displayTitle} />
        ) : (
          <RewardAsset assetId={reward.assetId} title={reward.title} />
        )
      )}
      <p className="text-sm font-semibold">{displayTitle}</p>
      <p className="mt-1 text-xs text-zinc-500">
        {status === "collected"
          ? "Collected"
          : status === "ready"
            ? "Ready"
            : reward.kind === "badge"
              ? "Earned automatically"
              : "Sealed"}
      </p>
      {lockedHint ? (
        <p className="mt-2 text-xs leading-5 text-zinc-600 dark:text-zinc-400">
          {lockedHint}
        </p>
      ) : null}
      {reward.kind === "chest" ? (
        <button
          type="button"
          disabled={status !== "ready"}
          className="btn-outline mt-3 w-full"
          onClick={() => onOpenChest(reward.rewardId)}
        >
          {status === "collected"
            ? "Opened"
            : status === "ready"
              ? "Open relic chest"
              : unlockLabel(reward)}
        </button>
      ) : (
        <p className="mt-3 text-xs text-zinc-500">
          {status === "collected"
            ? "Badge earned automatically."
            : locked
              ? unlockLabel(reward)
              : unlockLabel(reward)}
        </p>
      )}
    </div>
  );
}

function LockedRelicAsset({ title }: { title: string }) {
  return (
    <div className="mb-3 flex h-28 items-center justify-center overflow-hidden rounded-lg border border-dashed border-zinc-300 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-950">
      <img
        src="/assets/quests/common/progress-relic-chest-marker.webp"
        alt=""
        loading="lazy"
        decoding="async"
        className="h-16 w-16 grayscale opacity-60"
      />
      <span className="sr-only">{title}</span>
    </div>
  );
}

function lockedRewardHint(route: QuestRoute, reward: RouteReward): string {
  const unlock = unlockLabel(reward);
  if (reward.kind === "badge") {
    return `${unlock} The badge is awarded automatically when the route milestone is reached.`;
  }
  return `${unlock} ${routeRelicLore(route.routeId)}`;
}

function routeRelicLore(routeId: string): string {
  if (routeId === "mars-traverse") {
    return "The chest seal responds to steady progress across the red-planet route.";
  }
  if (routeId === "moon-trek") {
    return "The latch brightens as your lunar path closes in on the next cache.";
  }
  if (routeId === "under-the-sea") {
    return "The clasp opens only after enough distance through the blue expedition route.";
  }
  if (routeId === "four-moons-of-jupiter") {
    return "The lock is keyed to the next Jovian transfer milestone.";
  }
  if (routeId === "appalachian-trail" || routeId === "pacific-crest-trail") {
    return "The cache waits at a trail milestone farther along the route.";
  }
  if (routeId === "national-parks-grand-tour") {
    return "Each park chest opens when your walking reaches that park stop.";
  }
  if (routeId === "earth-city-tour") {
    return "Each city chest opens when your route reaches that city stop.";
  }
  return "Keep moving on this quest route to break the seal.";
}

type RewardStatus = "locked" | "ready" | "collected";

function rewardStatus(
  reward: RouteReward,
  progress: QuestProgress,
  state: QuestState,
): RewardStatus {
  const unlocked = progress.unlockedRewardIds.includes(reward.rewardId);
  const collected =
    reward.kind === "chest"
      ? state.openedChestIds.includes(reward.rewardId)
      : state.earnedBadges.some((badge) => badge.badgeId === reward.rewardId);
  if (collected) return "collected";
  return unlocked ? "ready" : "locked";
}

function syncHealthRows(
  state: QuestState,
  route: QuestRoute,
  healthDaily: HealthDailyResponse,
): QuestState {
  if (!state.activeQuest) return state;
  let ledger = state.ledger;
  for (const day of healthDaily.rows) {
    if (day.date < state.activeQuest.startDate) continue;
    const entry = createLedgerEntry({
      route,
      day,
      assignmentReason: "daily_sync",
      createdAt: new Date().toISOString(),
    });
    ledger = upsertDailyLedgerEntry(ledger, entry);
  }
  const progress = deriveQuestProgress(route, { ...state, ledger });
  const unlockedLandmarkIds = mergeUnique(
    state.unlockedLandmarkIds,
    progress.unlockedLandmarkIds,
  );
  const earnedBadges = mergeEarnedBadges(state, route, progress);
  if (
    ledger === state.ledger &&
    unlockedLandmarkIds.length === state.unlockedLandmarkIds.length &&
    earnedBadges.length === state.earnedBadges.length
  ) {
    return state;
  }
  return {
    ...state,
    ledger,
    unlockedLandmarkIds,
    earnedBadges,
    updatedAt: new Date().toISOString(),
  };
}

function mergeEarnedBadges(
  state: QuestState,
  route: QuestRoute,
  progress: QuestProgress,
): QuestState["earnedBadges"] {
  const existing = new Set(state.earnedBadges.map((badge) => badge.badgeId));
  const next = [...state.earnedBadges];
  for (const reward of route.rewards) {
    if (reward.kind !== "badge") continue;
    if (!progress.unlockedRewardIds.includes(reward.rewardId)) continue;
    if (existing.has(reward.rewardId)) continue;
    next.push({
      badgeId: reward.rewardId,
      routeId: route.routeId,
      routeVersionId: route.routeVersionId,
      earnedAt: new Date().toISOString(),
      title: reward.title,
    });
    existing.add(reward.rewardId);
  }
  return next;
}

function questUnlockHighlights(
  route: QuestRoute,
  previous: QuestState,
  next: QuestState,
): UnlockNotice | null {
  const previousProgress = deriveQuestProgress(route, previous);
  const nextProgress = deriveQuestProgress(route, next);
  const previousLandmarks = new Set(previousProgress.unlockedLandmarkIds);
  const previousRewards = new Set(previousProgress.unlockedRewardIds);
  const landmarkTitles = routeLandmarks(route)
    .filter(
      (landmark) =>
        nextProgress.unlockedLandmarkIds.includes(landmark.landmarkId) &&
        !previousLandmarks.has(landmark.landmarkId),
    )
    .map((landmark) => landmark.title);
  const newRewards = route.rewards.filter(
    (reward) =>
      nextProgress.unlockedRewardIds.includes(reward.rewardId) &&
      !previousRewards.has(reward.rewardId),
  );
  const relics = newRewards
    .filter((reward) => reward.kind === "chest" || reward.kind === "sight")
    .map((reward) => reward.title);
  const badges = newRewards
    .filter((reward) => reward.kind === "badge")
    .map((reward) => reward.title);
  if (landmarkTitles.length === 0 && relics.length === 0 && badges.length === 0) {
    return null;
  }
  return {
    routeTitle: route.title,
    landmarks: landmarkTitles,
    relics,
    badges,
  };
}

function unlockNoticeForProgressWindow(
  route: QuestRoute,
  previousMeters: number,
  nextMeters: number,
): UnlockNotice | null {
  if (nextMeters <= previousMeters) return null;
  const landmarks = routeLandmarks(route)
    .filter((landmark) => {
      const meter = landmarkMeter(route, landmark);
      return meter > previousMeters && meter <= nextMeters;
    })
    .map((landmark) => landmark.title);
  const rewards = route.rewards.filter((reward) => {
    const meter = rewardUnlockMeter(route, reward);
    return meter !== null && meter > previousMeters && meter <= nextMeters;
  });
  const relics = rewards
    .filter((reward) => reward.kind === "chest" || reward.kind === "sight")
    .map((reward) => reward.title);
  const badges = rewards
    .filter((reward) => reward.kind === "badge")
    .map((reward) => reward.title);
  if (landmarks.length === 0 && relics.length === 0 && badges.length === 0) {
    return null;
  }
  return { routeTitle: route.title, landmarks, relics, badges };
}

function QuestStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-800">
      <p className="text-xs font-medium text-zinc-500">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function QuestProgressBar({
  route,
  progress,
}: {
  route: QuestRoute;
  progress: QuestProgress;
}) {
  const routeDistance = routeDistanceMeters(route);
  const landmarkTicks =
    routeLandmarks(route).length <= 40
      ? routeLandmarks(route).map((landmark) => ({
          id: `landmark-${landmark.landmarkId}`,
          label: landmark.title,
          percent: routePercentForMeters(landmarkMeter(route, landmark), routeDistance),
          kind: "waypoint" as const,
        }))
      : [];
  const relicTicks = route.rewards
    .filter((reward) => reward.kind === "chest" || reward.kind === "badge")
    .map((reward) => ({
      id: `reward-${reward.rewardId}`,
      label: reward.title,
      percent: routePercentForReward(route, reward),
      kind: reward.kind === "badge" ? ("badge" as const) : ("relic" as const),
    }))
    .filter((tick): tick is {
      id: string;
      label: string;
      percent: number;
      kind: "badge" | "relic";
    } => tick.percent !== null);
  const ticks = [...landmarkTicks, ...relicTicks].sort(
    (a, b) => a.percent - b.percent,
  );

  return (
    <div className="relative h-5">
      <div className="absolute left-0 right-0 top-1 h-3 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
        <div
          className="h-full rounded-full bg-accent"
          style={{ width: `${progress.percentComplete}%` }}
        />
      </div>
      {ticks.map((tick) => {
        const reached = tick.percent <= progress.percentComplete;
        return (
          <span
            key={tick.id}
            title={`${tick.kind === "waypoint" ? "Waypoint" : tick.kind === "badge" ? "Badge" : "Relic"}: ${tick.label}`}
            className={`absolute top-0 z-10 -translate-x-1/2 transition ${
              reached ? "scale-110" : "opacity-70 grayscale"
            }`}
            style={{ left: `${tick.percent}%` }}
          >
            {tick.kind === "relic" ? (
              <img
                src="/assets/quests/common/progress-relic-chest-marker.webp"
                alt=""
                loading="lazy"
                decoding="async"
                className={`h-6 w-6 drop-shadow ${
                  reached ? "brightness-110 saturate-150" : ""
                }`}
              />
            ) : tick.kind === "badge" ? (
              <span
                className={`block h-4 w-4 rotate-45 rounded-[4px] border border-white shadow-sm dark:border-zinc-950 ${
                  reached ? "bg-accent" : "bg-amber-400"
                }`}
              />
            ) : (
              <span
                className={`block h-4 w-4 rounded-full border-2 shadow-sm dark:border-zinc-950 ${
                  reached
                    ? "border-cyan-100 bg-accent"
                    : "border-white bg-violet-300"
                }`}
              />
            )}
          </span>
        );
      })}
    </div>
  );
}

function UnlockNoticeCard({
  notice,
  onDismiss,
}: {
  notice: UnlockNotice;
  onDismiss: () => void;
}) {
  const highlights = [
    ...notice.landmarks.map((title) => `Waypoint reached: ${title}`),
    ...notice.relics.map((title) => `Relic chest unlocked: ${title}`),
    ...notice.badges.map((title) => `Badge earned: ${title}`),
  ].slice(0, 4);
  return (
    <aside className="fixed bottom-5 right-5 z-50 w-[min(360px,calc(100vw-2rem))] rounded-2xl border border-accent/40 bg-white p-4 shadow-2xl dark:bg-zinc-950">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Quest milestone
          </p>
          <h2 className="mt-1 text-lg font-semibold">{notice.routeTitle}</h2>
        </div>
        <button type="button" className="btn-ghost px-2 py-1" onClick={onDismiss}>
          Close
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {highlights.map((line) => (
          <p
            key={line}
            className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-800 dark:bg-zinc-900"
          >
            {line}
          </p>
        ))}
      </div>
      {(notice.landmarks.length + notice.relics.length + notice.badges.length) >
      highlights.length ? (
        <p className="mt-3 text-xs text-zinc-500">
          More milestones were added to your route log.
        </p>
      ) : null}
    </aside>
  );
}

function ImportButton({
  fileInputRef,
  onImport,
}: {
  fileInputRef: RefObject<HTMLInputElement>;
  onImport: (file: File) => void;
}) {
  return (
    <>
      <button
        type="button"
        className="btn-outline"
        onClick={() => fileInputRef.current?.click()}
      >
        Import
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json"
        className="hidden"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = "";
          if (file) onImport(file);
        }}
      />
    </>
  );
}

function RouteHero({ route }: { route: QuestRoute }) {
  const hero = route.shareMeta.ogImageAssetId
    ? assetPath(route.shareMeta.ogImageAssetId)
    : routeAssetPath(route, "hero");
  return (
    <div className="relative min-h-[220px] overflow-hidden bg-zinc-950">
      {hero ? (
        <img
          src={hero}
          alt={`${route.title} quest hero`}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(34,211,238,0.38),transparent_20%),radial-gradient(circle_at_30%_90%,rgba(255,255,255,0.3),transparent_18%),linear-gradient(135deg,#020617,#0f172a_48%,#111827)]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/10 to-transparent" />
      <div className="absolute bottom-7 left-6 text-white">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-cyan-200">
          {route.displayDistanceMode.replace("_", " ")}
        </p>
        <p className="mt-1 text-2xl font-semibold">{route.title}</p>
      </div>
    </div>
  );
}

function RouteThumbnail({ route }: { route: QuestRoute }) {
  const hero = route.shareMeta.ogImageAssetId
    ? assetPath(route.shareMeta.ogImageAssetId)
    : routeAssetPath(route, "hero");
  return (
    <div className="h-16 w-20 shrink-0 overflow-hidden rounded-lg bg-zinc-100 dark:bg-zinc-900">
      {hero ? (
        <img
          src={hero}
          alt={`${route.title} route thumbnail`}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      ) : null}
    </div>
  );
}

function RewardAsset({ assetId, title }: { assetId?: string; title: string }) {
  const path = assetId ? assetPath(assetId) : null;
  if (!path) return null;
  return (
    <div className="mb-3 flex h-28 items-center justify-center overflow-hidden rounded-lg bg-zinc-100 dark:bg-zinc-950">
      <img
        src={path}
        alt={title}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-contain p-2"
      />
    </div>
  );
}

function assetPath(assetId: string): string | null {
  return displayQuestAssetPath(assetId);
}

function routeAssetPath(route: QuestRoute, kind: RouteAssetRef["kind"]): string | null {
  const assetId = route.assets.find((asset) => asset.kind === kind)?.assetId;
  return assetId ? assetPath(assetId) : null;
}

function metersToLandmark(
  route: QuestRoute,
  landmark: RouteLandmark,
  meters: number,
): number {
  return Math.max(0, landmarkMeter(route, landmark) - meters);
}

function landmarkMeter(route: QuestRoute, landmark: RouteLandmark): number {
  if (typeof landmark.atMeters === "number") return landmark.atMeters;
  if (typeof landmark.atPercent === "number") {
    return routeDistanceMeters(route) * (landmark.atPercent / 100);
  }
  return 0;
}

function openedChestCount(route: QuestRoute, state: QuestState): number {
  const chestIds = new Set(
    route.rewards
      .filter((reward) => reward.kind === "chest")
      .map((reward) => reward.rewardId),
  );
  return state.openedChestIds.filter((rewardId) => chestIds.has(rewardId)).length;
}

function completionBadgeAssetId(route: QuestRoute): string | undefined {
  return route.rewards.find((reward) => reward.unlock.completion)?.assetId;
}

function routePercentForMeters(meters: number, routeDistance: number): number {
  if (routeDistance <= 0) return 0;
  return Math.min(100, Math.max(0, (meters / routeDistance) * 100));
}

function routePercentForReward(
  route: QuestRoute,
  reward: RouteReward,
): number | null {
  if (typeof reward.unlock.atPercent === "number") {
    return Math.min(100, Math.max(0, reward.unlock.atPercent));
  }
  if (typeof reward.unlock.atMeters === "number") {
    return routePercentForMeters(reward.unlock.atMeters, routeDistanceMeters(route));
  }
  if (reward.unlock.completion) return 100;
  return null;
}

function rewardUnlockMeter(route: QuestRoute, reward: RouteReward): number | null {
  if (typeof reward.unlock.atMeters === "number") return reward.unlock.atMeters;
  if (typeof reward.unlock.atPercent === "number") {
    return routeDistanceMeters(route) * (reward.unlock.atPercent / 100);
  }
  if (reward.unlock.completion) return routeDistanceMeters(route);
  return null;
}

function latestBadgeForRoute(
  state: QuestState | null,
  route: QuestRoute,
): QuestState["earnedBadges"][number] | null {
  return (
    state?.earnedBadges
      .filter((badge) => badge.routeVersionId === route.routeVersionId)
      .at(-1) ?? null
  );
}

function unlockLabel(reward: RouteReward): string {
  if (reward.unlock.completion) return "Unlocks on completion.";
  if (typeof reward.unlock.atPercent === "number") {
    return `Unlocks at ${reward.unlock.atPercent}%.`;
  }
  if (typeof reward.unlock.atMeters === "number") {
    return `Unlocks at ${fmtNumber(Math.round(reward.unlock.atMeters))} m.`;
  }
  return "Unlocks later.";
}

function dateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function mergeUnique(existing: string[], next: string[]): string[] {
  return Array.from(new Set([...existing, ...next]));
}

function downloadShareCard(summary: ShareSummary) {
  const svg = renderShareCardSvg(summary, "og");
  const blob = new Blob([svg], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = shareFilename(summary, "og");
  link.click();
  URL.revokeObjectURL(url);
}
